import clsx from "clsx"
import { forwardRef, useContext, useEffect, useId, useImperativeHandle, useMemo, useRef } from "react"
import { useFormContext } from "react-hook-form"

import { faIcon } from "@utils/faIcon"

import { Icon, Label } from "@atoms"

import { Dialog, Popover } from "@molecules"
import { FormControl, FormField, FormItem, useFormField } from "@molecules/Form/Form"
import type { InputName } from "@molecules/Form/inputs/Input/Input.types"
import InputInternalWrapper from "@molecules/Form/inputs/InputInternalWrapper/InputInternalWrapper"
import { PopoverContext } from "@molecules/Popover/Popover"
import { useSelectContext } from "@molecules/Select/Select.context"

import styles from "./SelectInput.module.scss"
import { SelectInputProps } from "./SelectInput.types"

function SelectInputComponent<T extends InputName = InputName>(
    props: SelectInputProps<T>,
    ref: React.Ref<HTMLButtonElement>,
) {
    const {
        selectedItem: selectedItemProp,
        size,
        className,
        isGrouped = false,
        groupPosition = "middle",

        placeholder,
        label,
        indicator,
        showLabel = true,
        suffix,
        name,

        isTabbable = true,
        isOpen,
        isDisabled = false,
        isRequired,
        isError,
        form,
        autoFocus,
        value,
        defaultValue,
    } = props

    const { mode, selectedItem, syncSubtextToSuffix } = useSelectContext()
    const effectiveSelectedItem = selectedItemProp ?? selectedItem

    const { isOpen: isPopoverOpen } = useContext(PopoverContext)

    const buttonRef = useRef<HTMLButtonElement>(null)
    useImperativeHandle(ref, () => buttonRef.current as HTMLButtonElement, [])

    const defaultId = useId()

    useEffect(() => {
        if (autoFocus && !isDisabled && buttonRef.current) {
            buttonRef.current.focus()
        }
    }, [autoFocus, isDisabled])

    const leadingVisualElement = useMemo(() => {
        if (effectiveSelectedItem?.leading) {
            return <div className={styles.leadingVisual}>{effectiveSelectedItem.leading}</div>
        } else {
            return null
        }
    }, [effectiveSelectedItem])

    const effectiveSuffix = suffix ?? (syncSubtextToSuffix ? effectiveSelectedItem?.subtext : undefined)

    const buttonContent = (
        <button
            id={defaultId}
            type="button"
            className={clsx(styles.base, className, {
                [styles.open]: isOpen,
                [styles.selected]: !!effectiveSelectedItem?.label,
                [styles.error]: isError,

                [styles.sizeSm]: size === "sm",
                [styles.sizeMd]: size === "md",
                [styles.grouped]: isGrouped,
                [styles.groupedStart]: isGrouped && groupPosition === "start",
                [styles.groupedMiddle]: isGrouped && groupPosition === "middle",
                [styles.groupedEnd]: isGrouped && groupPosition === "end",
            })}
            disabled={isDisabled}
            tabIndex={isTabbable ? 0 : -1}
            ref={buttonRef}
            onKeyDown={(event) => {
                // Prevent cmdk from handling keyboard events when the select isn't open,
                // which would crash trying to iterate over unmounted items (arrow keys)
                // or swallow Enter/Space before the browser can fire the click to open the popover.
                if (
                    !isPopoverOpen &&
                    (event.code === "ArrowUp" ||
                        event.code === "ArrowDown" ||
                        event.code === "Enter" ||
                        event.code === "NumpadEnter" ||
                        event.code === "Space")
                ) {
                    event.stopPropagation()
                }
            }}
        >
            <div className={styles.iconAndText}>
                {leadingVisualElement}
                {showLabel && (
                    <div className={clsx(styles.text, { [styles.placeholder]: !effectiveSelectedItem?.label })}>
                        {effectiveSelectedItem?.label ? effectiveSelectedItem.label : placeholder}
                    </div>
                )}
            </div>

            {effectiveSuffix && <div className={styles.suffix}>{effectiveSuffix}</div>}

            <div className={styles.arrow}>
                <Icon icon={faIcon({ name: "angles-up-down", style: "regular" })} size={12} container="fixedHeight" />
            </div>
        </button>
    )

    const submittedValue = (value ?? effectiveSelectedItem?.value ?? defaultValue ?? "") as string

    const triggerElement =
        mode === "dialog" ? (
            <Dialog.Trigger>{buttonContent}</Dialog.Trigger>
        ) : (
            <Popover.Trigger>{buttonContent}</Popover.Trigger>
        )

    const labelIsString = typeof label === "string"

    return (
        <InputInternalWrapper
            isDisabled={isDisabled}
            inputHelpText={isGrouped ? null : props.inputHelpText}
            indicator={indicator}
            label={
                label && !isGrouped ? (
                    <Label
                        {...(labelIsString ? { label } : label)}
                        htmlFor={defaultId}
                        isDisabled={isDisabled}
                        variant="subtle"
                    />
                ) : undefined
            }
        >
            <>
                {name && (
                    <input
                        type="hidden"
                        name={name}
                        value={submittedValue}
                        form={form}
                        disabled={isDisabled}
                        required={isRequired}
                    />
                )}
                {triggerElement}
            </>
        </InputInternalWrapper>
    )
}

const withController = <T extends InputName>(Component: React.FC<SelectInputProps<T>>) => {
    const Controller = forwardRef<HTMLButtonElement, SelectInputProps<T>>((props, ref) => {
        const { ...rest } = props
        const form = useFormContext<Record<string, unknown>>()
        const { isOpen } = useContext(PopoverContext)

        if (props.isControlled !== false) {
            return (
                <FormField
                    control={form.control}
                    name={props.name}
                    render={({ field }) => {
                        // Sync external selected item back into react-hook-form
                        useEffect(() => {
                            if (props.value !== undefined && props.name) {
                                form.setValue(props.name as unknown as string, props.value)
                            }
                        }, [props.value, props.name])

                        // When selection changes in context, push it into RHF without triggering validation
                        const { selectedItem, setSelectedItem } = useSelectContext()
                        const hasPushedInitial = useRef(false)
                        const hasInitializedFromForm = useRef(false)

                        // Initialize context's selectedItem from form's default value using options prop
                        useEffect(() => {
                            if (hasInitializedFromForm.current || !props.options || !field.value) {
                                return
                            }
                            const matchingOption = props.options.find((opt) => opt.value === field.value)
                            if (matchingOption) {
                                setSelectedItem(matchingOption)
                                hasInitializedFromForm.current = true
                            }
                        }, [field.value, props.options, setSelectedItem])

                        useEffect(() => {
                            if (!props.name || selectedItem?.value == null) {
                                return
                            }

                            const fieldName = props.name as unknown as string
                            if (!hasPushedInitial.current) {
                                form.setValue(fieldName, selectedItem.value)
                                hasPushedInitial.current = true
                            } else {
                                form.setValue(fieldName, selectedItem.value)
                            }
                        }, [selectedItem?.value, props.name])

                        // Trigger validation when the Select (popover/dialog) closes
                        const prevIsOpenRef = useRef<boolean>(Boolean(isOpen))
                        useEffect(() => {
                            if (!props.name) {
                                return
                            }
                            const wasOpen = prevIsOpenRef.current
                            if (wasOpen && !isOpen) {
                                const fieldName = props.name as unknown as string
                                void form.trigger(fieldName)
                            }
                            prevIsOpenRef.current = Boolean(isOpen)
                        }, [isOpen, props.name])

                        const { invalid, error } = useFormField()

                        return (
                            <FormItem>
                                <FormControl>
                                    <Component
                                        {...rest}
                                        {...field}
                                        isError={invalid || rest.isError}
                                        inputHelpText={
                                            error?.message
                                                ? {
                                                      message: String(error.message),
                                                      status: "error",
                                                      showIcon: true,
                                                  }
                                                : rest.inputHelpText
                                        }
                                        onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
                                            const nextElement = event.relatedTarget as HTMLElement | null
                                            if (nextElement && nextElement.dataset.preventinputvalidation) {
                                                // Skip validation if the clicked element contains preventInputValidation
                                                return
                                            } else {
                                                field.onBlur()
                                                props.onBlur?.(event)
                                            }
                                        }}
                                        isControlled={true}
                                        options={props.options}
                                        value={props.value ?? (field.value as string)}
                                        ref={ref}
                                    />
                                </FormControl>
                            </FormItem>
                        )
                    }}
                />
            )
        } else {
            return <Component {...(rest as SelectInputProps<T>)} ref={ref} />
        }
    })

    Controller.displayName = "Input"

    return Controller as typeof SelectInputComponent
}

export default withController(forwardRef(SelectInputComponent))
